/**
 * Motor de cálculo del cotizador.
 *
 * Módulo puro: no toca React, no toca Firebase, no lee el reloj. Recibe datos y
 * devuelve números. Eso permite probarlo solo y es la razón por la que sobrevive
 * a cualquier cambio de plataforma.
 *
 * Reproduce el modelo de la plataforma de SkyPel, documentado en
 * `areas/Sistemas/Especificacion_Cotizador_desde_SkyPel.md`. Tres reglas de diseño
 * que no se negocian:
 *
 *  1. Un producto es una receta, no un artículo: su precio sale de sus insumos y
 *     sus minutos.
 *  2. El costo de un renglón depende del total de los demás: los días salen de la
 *     suma de minutos de TODA la cotización, y el traslado sale de los días.
 *  3. Corren dos contabilidades en paralelo: la que ve el cliente y la que protege
 *     el margen. La segunda puede modificar a la primera vía el factor UAFIR, nunca
 *     al revés.
 *
 * Las etapas 1, 2 y 3 son ACUMULATIVAS, no alternativas: la 1 es la propuesta
 * económica, 1+2 la intermedia y las tres el proyecto completo.
 */

export const FACTORES_BASE = {
  IVA: 1.16,
  TDC: 1.0,
  UtilidadComplementos: 1.45,
  UtilidadConsumibles: 1.45,
  UtilidadManoDeObra: 1.6,
  UtilidadTraslado: 1.35,
  UtilidadPeajesYHospedajes: 1.2,
  minutoManoDeObra: 3.5,
  MinutosDia: 480,
  CostoTDC: 0.036,              // comisión de la terminal bancaria
  CostoDiaMetropolitano: 2800,  // gasto fijo diario que se prorratea por el `factor` del producto
  gastosAdminPct: 10,           // % sobre el gasto de operación
  comisionVentaPct: 5,          // % sobre el precio de venta
  CostoGasolina: 24.5,
  RendimientoGasolina: 11,
  CostoDesgasteTransporte: 1.8,
  CostoMinutoTraslado: 2.2
}

const vacio = () => ({ producto: 0, complementos: 0, consumibles: 0, manoObra: 0, minutos: 0 })
export const subtotal = v => v.producto + v.complementos + v.consumibles + v.manoObra

/** Contexto que necesita el motor: catálogo y factores ya resueltos. */
function ctx({ productos, dispositivos, factores }) {
  const P = new Map((productos || []).map(p => [p.sku, p]))
  const D = new Map((dispositivos || []).map(d => [d.sku, d]))
  return { P, D, f: { ...FACTORES_BASE, ...(factores || {}) } }
}

/**
 * Nivel 1 — por producto.
 * Separa consumibles de complementos según el tipo del insumo, cada bolsa con su
 * propia utilidad. Devuelve además el costo desnudo, que usa la contabilidad interna.
 */
export function porProducto(item, c) {
  const b = c.P.get(item.sku) || { comp: [], precio: 0, tIns: 0, tCfg: 0, factor: 0 }
  let costoComplementos = 0
  let costoConsumibles = 0

  for (const ins of b.comp || []) {
    const d = c.D.get(ins.sku)
    if (!d) continue
    // Los precios de proveedor que ya vienen con IVA se normalizan a costo sin IVA
    const costo = d.iva ? d.costo / c.f.IVA : d.costo
    if (d.tipo === 'Consumibles') costoConsumibles += costo * ins.c
    else costoComplementos += costo * ins.c
  }

  const q = item.q || 0
  const minutos = (b.tIns + (b.tCfg || 0)) * q

  return {
    producto: Math.ceil((b.precio || 0) * q),
    complementos: Math.ceil(costoComplementos * q * c.f.UtilidadComplementos * c.f.IVA * c.f.TDC),
    consumibles: Math.ceil(costoConsumibles * q * c.f.UtilidadConsumibles * c.f.IVA * c.f.TDC),
    manoObra: Math.ceil(minutos * c.f.minutoManoDeObra * c.f.UtilidadManoDeObra * c.f.IVA * c.f.TDC),
    minutos,
    // Costo desnudo, para la contabilidad interna
    costoMaterial: (costoComplementos + costoConsumibles) * q,
    costoMano: minutos * c.f.minutoManoDeObra,
    factor: (b.factor || 0) * q
  }
}

/**
 * Nivel 2 y 3 — por habitación y por cotización.
 * `factorForzado` existe para romper la recursión: la contabilidad interna necesita
 * los totales a precio de lista, sin el ajuste que ella misma produce.
 */
export function porCotizacion(cot, c, factorForzado) {
  const r = { 1: vacio(), 2: vacio(), 3: vacio() }

  for (const hab of cot.habitaciones || []) {
    for (const item of hab.items || []) {
      const v = porProducto(item, c)
      for (const e of [1, 2, 3]) {
        if (!item['e' + e]) continue
        for (const k of Object.keys(r[e])) r[e][k] += v[k]
      }
    }
  }

  const km = (Number(cot.distancia) || 0) * 2
  const min = (Number(cot.minTraslado) || 0) * 2
  const trasladoPorDia = Math.ceil(
    ((c.f.CostoGasolina / c.f.RendimientoGasolina) * km +
      c.f.CostoDesgasteTransporte * km +
      c.f.CostoMinutoTraslado * min) *
      c.f.UtilidadTraslado * c.f.TDC * c.f.IVA
  )
  const peaje = Math.ceil((Number(cot.peaje) || 0) * c.f.UtilidadPeajesYHospedajes * c.f.TDC * c.f.IVA)

  const fac = factorForzado !== undefined ? factorForzado : factorPrecio(cot, c)
  const o = {}

  for (const e of [1, 2, 3]) {
    const dias = Math.ceil(r[e].minutos / c.f.MinutosDia) || 0
    const activa = subtotal(r[e]) > 0
    o[e] = {
      producto: Math.round(r[e].producto * fac),
      complementos: Math.round(r[e].complementos * fac),
      consumibles: Math.round(r[e].consumibles * fac),
      manoObra: Math.round(r[e].manoObra * fac),
      minutos: r[e].minutos,
      dias,
      traslado: Math.round(trasladoPorDia * dias * fac),
      // Las casetas y el hospedaje se cobran una vez por etapa activa:
      // cada etapa es un despliegue distinto, con su propio viaje.
      peaje: activa ? Math.round(peaje * fac) : 0
    }
    o[e].total = subtotal(o[e]) + o[e].traslado + o[e].peaje
  }

  o.total = {}
  for (const k of ['producto','complementos','consumibles','manoObra','minutos','dias','traslado','peaje','total'])
    o.total[k] = o[1][k] + o[2][k] + o[3][k]
  o.factor = fac
  return o
}

/**
 * Contabilidad interna y protección de rentabilidad.
 *
 * UAFIR = precio − (gasto de operación + gastos de administración + comisión de venta)
 *
 * Si el UAFIR queda por debajo del objetivo, se despeja el precio que sí lo alcanza:
 *
 *   P = (1 + admin%) · GastoOperación ÷ (1 − comisiónVenta% − UAFIRobjetivo)
 *
 * Es la misma fórmula de `CalculoUAFIR` del sistema original, reescrita sin el
 * doble negativo. Verificada contra su álgebra.
 */
/** Venta y costo de operación de un subconjunto de bloques (etapas u opciones). */
function ventaYCosto(cot, c, bloques, base) {
  let venta = 0
  for (const e of bloques) venta += base[e].total

  let costoOp = 0
  for (const hab of cot.habitaciones || []) {
    for (const item of hab.items || []) {
      if (!bloques.some(e => item['e' + e])) continue
      const v = porProducto(item, c)
      costoOp += v.costoMaterial                       // material a costo
      costoOp += v.costoMano                           // cuadrilla a costo
      costoOp += subtotal(v) * c.f.CostoTDC            // comisión de la terminal bancaria
      costoOp += c.f.CostoDiaMetropolitano * v.factor  // prorrateo del gasto fijo
    }
  }
  // El transporte también es costo, pero a COSTO: se le retira la utilidad, el IVA y el tipo de cambio
  for (const e of bloques) {
    costoOp += base[e].traslado / (c.f.UtilidadTraslado * c.f.IVA * c.f.TDC)
    costoOp += base[e].peaje / (c.f.UtilidadPeajesYHospedajes * c.f.IVA * c.f.TDC)
  }
  return { venta, costoOp }
}

function evaluar(venta, costoOp, c, objetivo) {
  const gastosAdmin = costoOp * (c.f.gastosAdminPct / 100)
  const comisionVenta = venta * (c.f.comisionVentaPct / 100)
  const uafir = venta - (costoOp + gastosAdmin + comisionVenta)
  const uafirPct = venta ? (uafir / venta) * 100 : 0

  let factorUAFIR = 1
  if (venta > 0 && uafirPct < objetivo) {
    const divisor = 1 - c.f.comisionVentaPct / 100 - objetivo / 100
    if (divisor > 0) {
      const precioDeseado = ((1 + c.f.gastosAdminPct / 100) * costoOp) / divisor
      if (precioDeseado > 0) factorUAFIR = precioDeseado / venta
    }
  }
  return { venta, costoOp, gastosAdmin, comisionVenta, utilidad: venta - costoOp,
    uafir, uafirPct, factorUAFIR }
}

export function rentabilidad(cot, c) {
  const base = porCotizacion(cot, c, 1) // a precio de lista, sin realimentación
  const objetivo = cot.uafir !== undefined ? cot.uafir : 22
  const activos = [1, 2, 3].filter(e => base[e].total > 0)

  // En modo ETAPAS el cliente compra las tres en secuencia: la rentabilidad se mide
  // sobre el proyecto completo.
  if (cot.modo !== 'opciones') {
    const { venta, costoOp } = ventaYCosto(cot, c, [1, 2, 3], base)
    return { ...evaluar(venta, costoOp, c, objetivo), objetivo, dias: base.total.dias, porBloque: null }
  }

  // En modo OPCIONES el cliente elige UNA. Medir sobre la suma no significa nada.
  // Se evalúa cada opción por separado y manda la peor: así ninguna se vende por
  // debajo del objetivo, sea cual sea la que el cliente escoja.
  const porBloque = {}
  for (const e of activos) {
    const { venta, costoOp } = ventaYCosto(cot, c, [e], base)
    porBloque[e] = { ...evaluar(venta, costoOp, c, objetivo), objetivo, dias: base[e].dias }
  }
  const lista = Object.values(porBloque)
  if (!lista.length)
    return { venta: 0, costoOp: 0, gastosAdmin: 0, comisionVenta: 0, utilidad: 0,
      uafir: 0, uafirPct: 0, objetivo, factorUAFIR: 1, dias: 0, porBloque }

  const peor = lista.reduce((a, b) => (b.uafirPct < a.uafirPct ? b : a))
  return { ...peor, objetivo, dias: Math.max(...lista.map(x => x.dias)), porBloque }
}

/** Factor final que se aplica al precio: protección de rentabilidad × sobreprecio manual. */
export function factorPrecio(cot, c) {
  const f = cot.aplicarUAFIR === false ? 1 : rentabilidad(cot, c).factorUAFIR
  return f * (1 + (Number(cot.sobrePrecio) || 0) / 100)
}

/**
 * Lista de materiales consolidada: suma el mismo insumo aunque esté en habitaciones
 * distintas. Es la función que en la web original quedó comentada y solo sobrevivió
 * en la app móvil.
 */
export function materiales(cot, c, etapas = [1, 2, 3]) {
  const m = new Map()
  for (const hab of cot.habitaciones || []) {
    for (const item of hab.items || []) {
      if (!etapas.some(e => item['e' + e])) continue
      const b = c.P.get(item.sku)
      if (!b) continue
      for (const ins of b.comp || []) {
        const d = c.D.get(ins.sku)
        if (!d) continue
        if (!m.has(ins.sku))
          m.set(ins.sku, {
            sku: ins.sku, nombre: d.nombre, tipo: d.tipo, q: 0,
            costo: d.iva ? d.costo / c.f.IVA : d.costo
          })
        m.get(ins.sku).q += ins.c * (item.q || 0)
      }
    }
  }
  return [...m.values()].sort((a, b) =>
    a.tipo === b.tipo ? a.nombre.localeCompare(b.nombre) : a.tipo.localeCompare(b.tipo))
}

/** Productos de una etapa, agrupados por sku, para las miniaturas de la propuesta. */
export function itemsDeEtapa(cot, e) {
  const m = new Map()
  for (const hab of cot.habitaciones || [])
    for (const item of hab.items || [])
      if (item['e' + e]) m.set(item.sku, (m.get(item.sku) || 0) + item.q)
  return [...m.entries()].map(([sku, q]) => ({ sku, q }))
}

export { ctx as contexto }
